import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { KPICell, SectionFrame } from '../scheduled/shared';
import type { DeliveryMetrics } from '../../../lib/api/scheduled-dashboard';
import type { POLineDashboardData, POLineDrilldownMode } from '../../../lib/api/po-line-dashboard';

/**
 * Current Performance read from the monthly supplier files carries no order
 * value (the files have none), so money tiles and value charts would sit at
 * zero. These stand in for them with what the files do hold: lines delivered,
 * and how many were late.
 */

/** Whether the payload was built from the supplier files rather than live PO lines. */
export function isSupplierFileData(data: POLineDashboardData): boolean {
  return data.source === 'supplier_uploads';
}

/** Late = judged but not on time; not judged = no agreed or delivered date to compare. */
export function lineCounts(delivery: DeliveryMetrics) {
  const judged = delivery.otd_due ?? delivery.total_due;
  return {
    delivered: delivery.total_due,
    onTime: delivery.otd_met,
    late: Math.max(judged - delivery.otd_met, 0),
    notJudged: Math.max(delivery.total_due - judged, 0),
  };
}

interface VolumeFrameProps {
  delivery: DeliveryMetrics;
  subtitle: string;
  onOpen?: (mode: POLineDrilldownMode) => void;
}

/** Replaces the Cost frame: lines delivered, late lines, lines without dates to judge. */
export function SupplierFileVolumeFrame({ delivery, subtitle, onOpen }: VolumeFrameProps) {
  const c = lineCounts(delivery);
  const judged = c.onTime + c.late;
  const latePct = judged > 0 ? Math.round((c.late / judged) * 100) : 0;
  const count = (n: number) => <span className="text-2xl font-semibold tabular-nums">{n.toLocaleString()}</span>;
  return (
    <SectionFrame title="Volume" accent="blue" subtitle={subtitle}>
      <div className="grid grid-cols-3 gap-3" data-testid="supplier-file-volume">
        <KPICell label="Lines delivered" value={count(c.delivered)} foot="from the supplier files"
                 onClick={onOpen ? () => onOpen('due') : undefined} />
        <KPICell label="Late lines" value={count(c.late)} foot={`${latePct}% of judged lines`}
                 onClick={onOpen ? () => onOpen('otd_missed') : undefined} />
        <KPICell label="Not judged" value={count(c.notJudged)} foot="no dates to compare" />
      </div>
    </SectionFrame>
  );
}

export interface LinesChartRow {
  idx: number;
  label: string;
  onTime: number;
  late: number;
  /** Running total of lines delivered, when the chart shows one. */
  cumulative?: number;
}

interface LinesChartProps {
  title: string;
  description: string;
  rows: LinesChartRow[];
  /** Draw the running total as a line on a second axis. */
  showCumulative?: boolean;
  heightClass?: string;
  onBarClick?: (mode: POLineDrilldownMode, idx: number) => void;
}

/** Replaces a value chart: lines delivered per period, on time vs late. */
export function SupplierFileLinesChart({
  title, description, rows, showCumulative = false, heightClass = 'h-64', onBarClick,
}: LinesChartProps) {
  const click = (mode: POLineDrilldownMode) => (raw: unknown) => {
    const payload = raw && typeof raw === 'object' ? ((raw as { payload?: unknown }).payload ?? raw) : undefined;
    const idx = payload && typeof payload === 'object' ? (payload as { idx?: unknown }).idx : undefined;
    if (onBarClick && typeof idx === 'number') onBarClick(mode, idx);
  };
  const empty = rows.every((r) => r.onTime === 0 && r.late === 0);
  return (
    <Card className="border-slate-200" data-testid="supplier-file-lines-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-slate-700">{title}</CardTitle>
        <p className="text-[11px] text-muted-foreground">{description}</p>
      </CardHeader>
      <CardContent>
        {empty ? (
          <div className={`${heightClass} flex items-center justify-center text-sm text-muted-foreground`}>
            No delivery lines reported for this period.
          </div>
        ) : (
          <div className={heightClass}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={rows} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false}
                       tickFormatter={(v: number) => v.toLocaleString()} />
                {showCumulative && (
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false}
                         tickLine={false} tickFormatter={(v: number) => v.toLocaleString()} />
                )}
                <Tooltip cursor={{ fill: '#e2e8f0', fillOpacity: 0.7 }} contentStyle={{ fontSize: 12, borderRadius: 6 }}
                         formatter={(v: number) => v.toLocaleString()} />
                <Bar yAxisId="left" dataKey="onTime" stackId="lines" fill="#16a34a" name="On time"
                     cursor={onBarClick ? 'pointer' : undefined} onClick={click('otd_met')} activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }} />
                <Bar yAxisId="left" dataKey="late" stackId="lines" fill="#dc2626" name="Late" radius={[3, 3, 0, 0]}
                     cursor={onBarClick ? 'pointer' : undefined} onClick={click('otd_missed')} activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }} />
                {showCumulative && (
                  <Line yAxisId="right" type="monotone" dataKey="cumulative" stroke="#0f172a" strokeWidth={2.5}
                        dot={{ r: 3 }} name="Running total" />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
